import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Search, Menu, ArrowRight, Sun } from 'lucide-react';

const StanfordUniversity: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Stanford University";

    return (
        <div className="min-h-screen bg-white font-sans text-[#2e2d29] selection:bg-[#8C1515] selection:text-white">
            {/* Top Identity Bar */}
            <div className="bg-[#8C1515] text-white py-1.5 px-6">
                <div className="max-w-[1400px] mx-auto text-sm font-semibold">
                    {schoolName}
                </div>
            </div>

            {/* Main Nav */}
            <nav className="bg-white border-b border-gray-100 py-6 px-6 sticky top-0 z-50">
                <div className="max-w-[1400px] mx-auto flex justify-between items-center">
                    <div className="font-serif text-3xl font-normal tracking-wide text-[#8C1515]">
                        <span className="flex items-center gap-2">{schoolName.split(' ')[0]} <span className="font-sans font-light text-[#2e2d29] text-xl hidden md:inline ml-2 border-l border-gray-300 pl-4">{schoolName.split(' ').slice(1).join(' ') || "Home of Champions"}</span></span>
                    </div>

                    <div className="hidden lg:flex gap-10 font-sans text-lg font-normal text-[#2e2d29]">
                        <a href="#" className="hover:text-[#8C1515] hover:underline underline-offset-4 decoration-2">News</a>
                        <a href="#" className="hover:text-[#8C1515] hover:underline underline-offset-4 decoration-2">Events</a>
                        <a href="#" className="hover:text-[#8C1515] hover:underline underline-offset-4 decoration-2">Academics</a>
                        <a href="#" className="hover:text-[#8C1515] hover:underline underline-offset-4 decoration-2">Research</a>
                        <a href="#" className="hover:text-[#8C1515] hover:underline underline-offset-4 decoration-2">Health Care</a>
                    </div>

                    <div className="flex items-center gap-6">
                        <div className="hidden md:flex items-center gap-2 text-sm font-bold text-[#8C1515] uppercase tracking-wider cursor-pointer group">
                             Search <Search size={18} className="group-hover:scale-110 transition-transform" />
                        </div>
                        <div className="hidden md:flex items-center gap-2 text-sm font-bold text-[#8C1515] uppercase tracking-wider cursor-pointer group">
                             Menu <Menu size={18} className="group-hover:scale-110 transition-transform" />
                        </div>
                    </div>
                </div>
            </nav>

            {/* Hero - Full Screen Immersive */}
            <header className="relative h-[85vh] overflow-hidden">
                <img 
                    src="https://images.unsplash.com/photo-1627443190823-3b1df2cfa306?q=80&w=2070&auto=format&fit=crop" 
                    alt="Stanford Main Quad" 
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/20"></div>
                
                <div className="absolute bottom-24 left-0 w-full px-6 md:px-12">
                     <div className="max-w-[1400px] mx-auto">
                         <h1 className="text-6xl md:text-8xl font-serif text-white mb-6 drop-shadow-md">
                             The Winds of <br/> Freedom Blow.
                         </h1>
                         <div className="flex flex-col md:flex-row gap-4">
                             <Button className="bg-[#8C1515] hover:bg-white hover:text-[#8C1515] text-white font-sans font-bold text-lg h-14 px-8 rounded-full transition-all">
                                 Apply to {schoolName}
                             </Button>
                             <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#8C1515] font-sans font-bold text-lg h-14 px-8 rounded-full bg-transparent transition-all">
                                 Virtual Tour
                             </Button>
                         </div>
                     </div>
                </div>
            </header>

            {/* News Cards - Spacious */}
            <section className="py-32 px-6 md:px-12 max-w-[1400px] mx-auto">
                <div className="flex justify-between items-baseline mb-16">
                    <h2 className="text-4xl font-serif text-[#2e2d29]">Top Stories</h2>
                    <a href="#" className="text-[#8C1515] font-bold flex items-center gap-2 hover:underline">View All News <ArrowRight size={20} /></a>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12 text-[#2e2d29]">
                    {[
                        { img: "https://images.unsplash.com/photo-1532094349884-543bc11b234d?q=80&w=2070&auto=format&fit=crop", title: "Human-centered AI", desc: "Stanford HAI releases 2025 AI Index Report, detailing the rapid technical progress and societal impact of generative models." },
                        { img: "https://images.unsplash.com/photo-1605901309584-818e25960b8f?q=80&w=2678&auto=format&fit=crop", title: "Renewable Energy", desc: "Doerr School of Sustainability launches new initiative to accelerate carbon capture technology deployment." },
                        { img: "https://images.unsplash.com/photo-1511551203524-9a24350a5771?q=80&w=2070&auto=format&fit=crop", title: "Community", desc: "Cardinal Service program engages huge percentage of undergrads in public service pathways." }
                    ].map((item, i) => (
                        <div key={i} className="group cursor-pointer">
                            <div className="overflow-hidden rounded-md mb-6 shadow-sm">
                                <img src={item.img} className="w-full aspect-[4/3] object-cover hover:scale-105 transition-transform duration-500" alt="News" />
                            </div>
                            <h3 className="text-2xl font-bold mb-4 font-serif group-hover:text-[#8C1515] transition-colors leading-tight">{item.title}</h3>
                            <p className="text-lg text-gray-600 leading-relaxed font-light">{item.desc}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Campus Life / Sun */ }
            <section className="bg-[#f4f4f4] py-32 px-6 md:px-12">
                <div className="max-w-[1200px] mx-auto text-center">
                    <Sun size={64} className="mx-auto text-[#E98300] mb-8" />
                    <h2 className="text-4xl md:text-6xl font-serif text-[#2e2d29] mb-8">
                        A place for discovery, creativity, and innovation.
                    </h2>
                    <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-12">
                        Located in the heart of Silicon Valley, our sprawling campus offers endless opportunities to collaborate and create.
                    </p>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
                         {[
                             { num: "7", label: "Schools" },
                             { num: "8,180", label: "Undergraduates" },
                             { num: "9,000+", label: "Grad Students" },
                             { num: "2,300", label: "Faculty Members" }
                         ].map((stat, i) => (
                             <div key={i} className="p-8 bg-white shadow-sm rounded-lg hover:-translate-y-2 transition-transform duration-300">
                                 <div className="text-4xl font-bold text-[#8C1515] mb-2">{stat.num}</div>
                                 <div className="text-sm font-bold uppercase tracking-widest text-gray-500 font-sans">{stat.label}</div>
                             </div>
                         ))}
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-[#2e2d29] text-white py-20 px-6 md:px-12">
                <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-5 gap-12">
                    <div className="md:col-span-1">
                        <div className="bg-[#8C1515] w-12 h-12 flex items-center justify-center mb-6">
                            <span className="font-serif font-bold text-2xl">S</span>
                        </div>
                        <p className="text-sm text-gray-300 font-sans">
                            {schoolName} <br/>
                            Stanford, California 94305
                        </p>
                    </div>

                    <div className="md:col-span-1">
                        <h4 className="font-bold mb-4">Schools</h4>
                        <ul className="space-y-2 text-sm text-gray-300">
                            <li><a href="#" className="hover:text-white">Business</a></li>
                            <li><a href="#" className="hover:text-white">Education</a></li>
                            <li><a href="#" className="hover:text-white">Engineering</a></li>
                            <li><a href="#" className="hover:text-white">Humanities & Sciences</a></li>
                            <li><a href="#" className="hover:text-white">Law</a></li>
                            <li><a href="#" className="hover:text-white">Medicine</a></li>
                            <li><a href="#" className="hover:text-white">Sustainability</a></li>
                        </ul>
                    </div>

                    <div className="md:col-span-3">
                         <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
                             <div>
                                 <h4 className="font-bold mb-4 font-serif">Resources</h4>
                                 <ul className="space-y-2 text-sm text-gray-300">
                                     <li><a href="#" className="hover:text-white">Campus Map</a></li>
                                     <li><a href="#" className="hover:text-white">Community</a></li>
                                     <li><a href="#" className="hover:text-white">Careers</a></li>
                                 </ul>
                             </div>
                             <div>
                                 <h4 className="font-bold mb-4 font-serif">About</h4>
                                 <ul className="space-y-2 text-sm text-gray-300">
                                     <li><a href="#" className="hover:text-white">History</a></li>
                                     <li><a href="#" className="hover:text-white">Administration</a></li>
                                     <li><a href="#" className="hover:text-white">Accreditation</a></li>
                                 </ul>
                             </div>
                         </div>
                         <div className="border-t border-gray-600 pt-8 flex flex-col md:flex-row justify-between text-xs text-gray-400">
                             <div className="flex gap-6 mb-4 md:mb-0">
                                 <a href="#" className="hover:text-white">Privacy</a>
                                 <a href="#" className="hover:text-white">Accessibility</a>
                                 <a href="#" className="hover:text-white">Nondiscrimination</a>
                             </div>
                             <p>© {schoolName}.</p>
                         </div>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default StanfordUniversity;
